import { IBaseResponse } from "@nkr/models/base-response";
import { IAccountsPayableOpo } from "@nkr/models/reports";
import { ICompBankInfo, ITransactionConfirmation } from "@nkr/models/reports/accounts-payable";
import { downloadFile } from "@nkr/services/download-file";
import { AuthActionCreators } from "@nkr/store/authorization";
import { useAppHookContext } from "@nkr/utils/rest-api/useAppHookContext";
import { useEffect, useState } from "react";
import * as Papa from 'papaparse';
import * as moment from 'moment';
import { toast } from 'react-toastify';

export interface ISemesterPayment {
    opoId: number;
    year: number;
    semester: number;
    amount: number;
}

interface IRetVal {
    list: IAccountsPayableOpo[],
    loading: boolean,
    error: string,
    compareBankInfo: (compBankInfos: ICompBankInfo[]) => void,
    updateAchInfo: (ap: IAccountsPayableOpo) => void,
    exportPaymentFile: (selected: IAccountsPayableOpo[]) => void,
    importConfirmationFile: (confirmations: ITransactionConfirmation[]) => ITransactionConfirmation[],
    markPaid: (achConfirmation: string, semesters: ISemesterPayment[]) => Promise<void>
}

function useOpoPayments(): IRetVal {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IAccountsPayableOpo[]>([]);

    useEffect(() => {
        setLoading(true);
        setError('');

        const fetchData = async () => {
            const config = {
                url: `${__API_BASE_}/admin/billing-report/accounts-payable-opo?payNowOnly=true`
            };

            try {
                const response: IBaseResponse = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.results && response.data.success) {
                    setList(response.data.results.reports);
                } else {
                    setError('Unable to load data');
                    setList([]);
                }
            } catch (err) {
                setList([]);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, []);

    const compareBankInfo = (compBankInfos: ICompBankInfo[]) => {
        const opoEntries = compBankInfos.filter(c => c.vendorName.endsWith(' OPO'));

        const clone = list.map(p => {
            const n = opoEntries.find(c => c.vendorName.slice(0, -4) === p.opoAlias.replace(/ /g, ''));
            if (n) {
                return { ...p, compAchAccountNumber: n.bankAccountNumber, compAchRoutingNumber: n.bankRoutingNumber };
            }
            return p;
        });
        setList(clone);
    }

    const updateAchInfo = async (ap: IAccountsPayableOpo) => {
        const config = {
            url: `${__API_BASE_}/admin/billing-report/opo/${ap.opoId}/ach`,
            data: {
                achRoutingNumber: ap.compAchRoutingNumber,
                achAccountNumber: ap.compAchAccountNumber
            }
        };

        try {
            const response: IBaseResponse = await dispatch(AuthActionCreators.apiPut(config));

            if (response && response.data.success) {
                const clone = list.map(p => {
                    if (p.opoId === ap.opoId) {
                        return { ...p, achRoutingNumber: ap.compAchRoutingNumber, achAccountNumber: ap.compAchAccountNumber };
                    }
                    return p;
                });
                setList(clone);
            }
        } catch (err) {
            setError(err);
        }
    }

    const exportPaymentFile = (selected: IAccountsPayableOpo[]) => {
        const invalid = selected.filter(p => !(
            p.achAccountNumber
            && p.achRoutingNumber
            && p.compAchAccountNumber
            && p.compAchRoutingNumber
            && p.achAccountNumber === p.compAchAccountNumber
            && p.achRoutingNumber === p.compAchRoutingNumber
        ));

        if (invalid.length > 0) {
            const opos = invalid.map(p => p.opoAlias).join(', ');
            toast.error(`Bank info missing or unverified for: ${opos}`);
            return;
        }

        const now = moment();
        const delivery = moment().add(1, 'days');
        const totalCents = selected.reduce((sum, p) => sum + Math.round(p.payNow * 100), 0);
        const TRANSACTION_START = 100200;

        const headerOneColumns = [
            'Indicator',
            'File ID (Modifier)',
            'File creation date',
            'File creation time',
            'Total trxn',
            'Total ACH credit amount',
            'Total ACH debit amount',
            'Batch Count'
        ];

        const headerOneData = [
            1,
            1,
            now.format('YYMMDD'),
            now.format('HHmm'),
            selected.length,
            totalCents,
            0,
            1,
        ];

        const headerTwoColumns = [
            'Indicator',
            'Service class code',
            'Chase Acct',
            'SEC Code',
            'Entry description',
            'Delivery by date',
            'Batch credit amount',
            'Batch debit amount',
            'Batch number',
            'Trxn in Batch'
        ];

        const headerTwoData = [
            5,
            220,
            956119850,
            'CCD',
            'OPO',
            delivery.format('YYMMDD'),
            totalCents,
            0,
            100,
            selected.length
        ];

        const mainColumns = [
            'Indicator',
            'Trxn Code',
            'Routing Num',
            'Acct number',
            'Trxn amount',
            'ID Number',
            'Payee name',
            'Trxn ID',
            'Addenda'
        ];

        const mainData = selected.map((pmt, i) => [
            6,
            22,
            pmt.achRoutingNumber,
            pmt.achAccountNumber,
            Math.round(pmt.payNow * 100),
            pmt.opoAlias.replace(/ /g, '').slice(0, 15),
            pmt.opoAlias.replace(/ /g, ''),
            TRANSACTION_START + i,
            'NKR OPO Payment',
        ]);

        const rows: any[][] = [
            headerOneColumns,
            headerOneData,
            headerTwoColumns,
            headerTwoData,
            mainColumns,
            ...mainData
        ];

        downloadFile(Papa.unparse(rows), `Chase_ACH_OPO_Upload_${now.format('YYYYMMDD')}.csv`, 'text/csv');
    }

    const importConfirmationFile = (confirmations: ITransactionConfirmation[]) => {
        const recs = confirmations.reduce((acc, v) => {
            const alias = v.payTo.replace(/\s+OPO$/i, '').replace(/\s/g, '');
            const i = acc.payments.findIndex(p => p.opoAlias.replace(/ /g, '') == alias);

            if (i < 0) {
                acc.unmatched.push(v);
            } else {
                acc.payments[i] = { ...acc.payments[i], confirmationAmount: Math.abs(v.amount), confirmationNumber: v.chaseTransactionNumber };
            }

            return acc;
        }, { unmatched: [] as ITransactionConfirmation[], payments: [...list] });

        setList(recs.payments);
        return recs.unmatched;
    }

    const markPaid = (achConfirmation: string, semesters: ISemesterPayment[]) => {
        const postData = async () => {
            const config = {
                url: `${__API__}/billing/opo-payment`,
                data: {
                    achConfirmation,
                    semesters
                }
            };

            try {
                const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.success) {
                    setList(prev => prev.filter(p => p.confirmationNumber !== achConfirmation));
                } else {
                    setError('An error occurred while trying to mark records as paid.');
                    toast.error(`An error occurred while trying to mark records as paid: ${response?.data?.message}`);
                }
            } catch (err) {
                setList([]);
                setError(err);
                toast.error(`An error occurred while trying to mark records as paid.`);
            }
        }

        return postData();
    }

    return { list, loading, error, compareBankInfo, updateAchInfo, exportPaymentFile, importConfirmationFile, markPaid };
}

export default useOpoPayments;
